import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen,
  Calendar,
  Clock,
  Search,
  ArrowRight,
  ArrowUpRight,
  X,
  Sparkles,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import { BLOG_CATEGORIES, knowledgeHub as BLOG_POSTS } from "../../Data/knowledgeHubData";

import "../../Style/resources/blog.css";

const POSTS_PER_PAGE = 6;

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeArticle, setActiveArticle] = useState(null);

  // Lock background body scroll when reader mode is open
  useEffect(() => {
    if (activeArticle) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeArticle]);

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setActiveArticle(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Filter logic
  const filteredPosts = useMemo(() => {
    return BLOG_POSTS.filter((post) => {
      const matchesCat =
        activeCategory === "All" || post.category === activeCategory;
      const matchesQuery =
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.category.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesQuery;
    });
  }, [activeCategory, searchQuery]);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchQuery]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE) || 1;
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const featuredPost = BLOG_POSTS.find((p) => p.featured) || BLOG_POSTS[0];

  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="blog-page">
      <Seo
        title="Our Blog & Market Intelligence | Conectify Tech"
        description="Explore the latest insights on B2B data sources, revenue growth, AI technologies, and high-ROI marketing strategies from Conectify Tech."
      />

      {/* Top Banner Header */}
      <section className="blog-banner">
        <Container>
          <div className="blog-banner__content">
            <span className="blog-banner__tag">
              <BookOpen size={14} /> Knowledge & Industry Analysis
            </span>
            <h1 className="blog-banner__title">Our Blog</h1>
            <p className="blog-banner__subtitle">
              Actionable perspectives on B2B sales intelligence, data architecture, startup growth, and ROI acceleration.
            </p>
          </div>
        </Container>
      </section>

      {/* Main Body */}
      <section className="blog-body">
        <Container>
          {/* Featured Article Hero (Shown on page 1 with no search filter) */}
          {currentPage === 1 && !searchQuery && activeCategory === "All" && featuredPost && (
            <div className="blog-featured">
              <div className="blog-featured__card">
                <div className="blog-featured__meta">
                  <span className="blog-badge blog-badge--featured">
                    <Sparkles size={12} /> Featured Insight
                  </span>
                  <span className="blog-badge">{featuredPost.category}</span>
                  <span className="blog-date">
                    <Calendar size={13} /> {featuredPost.date}
                  </span>
                </div>

                <h2 className="blog-featured__title">{featuredPost.title}</h2>
                <p className="blog-featured__excerpt">{featuredPost.excerpt}</p>

                <div className="blog-featured__footer">
                  <Button
                    icon={<ArrowRight size={18} />}
                    onClick={() => setActiveArticle(featuredPost)}
                  >
                    Read Full Article
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Search Bar & Category Filter Strip */}
          <div className="blog-controls">
            <div className="blog-search-box">
              <Search size={18} className="blog-search-icon" />
              <input
                type="text"
                placeholder="Search articles, trends, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="blog-search-input"
              />
            </div>

            <div className="blog-categories">
              {BLOG_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`blog-cat-pill ${activeCategory === cat ? "is-active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Posts Grid */}
          <div className="blog-grid">
            {paginatedPosts.length > 0 ? (
              paginatedPosts.map((post, idx) => (
                <motion.article
                  key={post.id}
                  className="blog-card"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.05 }}
                >
                  <div className="blog-card__header">
                    <span className="blog-card__tag">{post.category}</span>
                    <span className="blog-card__date">
                      <Calendar size={13} /> {post.date}
                    </span>
                  </div>

                  <h3 className="blog-card__title">{post.title}</h3>
                  <p className="blog-card__excerpt">{post.excerpt}</p>

                  <div className="blog-card__footer">
                    <button
                      type="button"
                      className="blog-card__read-btn"
                      onClick={() => setActiveArticle(post)}
                    >
                      <span>Read More</span>
                      <ArrowUpRight size={16} />
                    </button>
                    <span className="blog-card__readtime">
                      <Clock size={12} /> {post.readTime}
                    </span>
                  </div>
                </motion.article>
              ))
            ) : (
              <div className="blog-empty">
                <p>No articles found matching your criteria.</p>
                <button
                  type="button"
                  className="blog-reset-btn"
                  onClick={() => {
                    setActiveCategory("All");
                    setSearchQuery("");
                  }}
                >
                  Clear search and filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="blog-pagination">
              <button
                type="button"
                className="blog-page-nav"
                disabled={currentPage === 1}
                onClick={() => {
                  setCurrentPage((p) => Math.max(1, p - 1));
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }}
                aria-label="Previous page"
              >
                <ChevronLeft size={18} />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  className={`blog-page-num ${currentPage === num ? "is-active" : ""}`}
                  onClick={() => {
                    setCurrentPage(num);
                    window.scrollTo({ top: 400, behavior: "smooth" });
                  }}
                >
                  {num}
                </button>
              ))}

              <button
                type="button"
                className="blog-page-nav"
                disabled={currentPage === totalPages}
                onClick={() => {
                  setCurrentPage((p) => Math.min(totalPages, p + 1));
                  window.scrollTo({ top: 400, behavior: "smooth" });
                }}
                aria-label="Next page"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </Container>
      </section>

      {/* Reader Mode Modal */}
      <AnimatePresence>
        {activeArticle && (
          <div className="reader-backdrop" onClick={() => setActiveArticle(null)}>
            <motion.div
              className="reader-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 35, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 25, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Modal Header */}
              <div className="reader-modal__header">
                <div className="reader-modal__meta">
                  <span className="reader-modal__tag">{activeArticle.category}</span>
                  <span className="reader-modal__date">
                    <Calendar size={13} /> {activeArticle.date}
                  </span>
                  <span className="reader-modal__readtime">
                    <Clock size={13} /> {activeArticle.readTime}
                  </span>
                </div>

                <button
                  type="button"
                  className="reader-modal__close-btn"
                  onClick={() => setActiveArticle(null)}
                  aria-label="Close article"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Scrollable Article */}
              <div className="reader-modal__scroll">
                <h1 className="reader-modal__title">{activeArticle.title}</h1>

                <div className="reader-modal__content">
                  {activeArticle.content ? (
                    activeArticle.content.map((block, i) => {
                      if (block.type === "lead") {
                        return (
                          <p key={i} className="reader-p reader-p--lead">
                            {block.text}
                          </p>
                        );
                      }
                      if (block.type === "subheading") {
                        return (
                          <h4 key={i} className="reader-subheading">
                            {block.text}
                          </h4>
                        );
                      }
                      if (block.type === "point") {
                        return (
                          <div key={i} className="reader-point">
                            <div className="reader-point__badge">{block.number}</div>
                            <div className="reader-point__text">
                              <h5>{block.title}</h5>
                              <p>{block.text}</p>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <p key={i} className="reader-p">
                          {block.text}
                        </p>
                      );
                    })
                  ) : (
                    <p className="reader-p reader-p--lead">{activeArticle.excerpt}</p>
                  )}
                </div>

                {/* Bottom Call to Action */}
                <div className="reader-modal__footer">
                  <div className="reader-footer-copy">
                    <h6>Accelerate your pipeline with Conectify Tech</h6>
                    <p>Schedule a 15-minute consultation to audit your addressable market.</p>
                  </div>
                  <Button icon={<ArrowRight size={16} />} onClick={handleConsultation}>
                    Consult with an Expert
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}